import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiX, FiUpload, FiTruck, FiDroplet } from 'react-icons/fi';
import StockImportsManagement from './StockImportsManagement';
import SuppliesListView from './SuppliesListView';
import IngredientsListView from './IngredientsListView';
import ItemSearchSelect from './common/ItemSearchSelect';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

const InventoryManagement = ({ defaultTab = 'supplies' }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Update activeTab when defaultTab changes (from route)
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  // Supplies state
  const [supplies, setSupplies] = useState([]);
  const [showSupplyModal, setShowSupplyModal] = useState(false);
  const [editingSupply, setEditingSupply] = useState(null);
  const [supplyForm, setSupplyForm] = useState({
    name: '',
    unit: 'piece',
    piecesPerUnit: 1,
    quantity: 0,
    options: [],
    minStock: 0,
    recipeUnit: 'unit',
    defaultPrice: 0,
    notes: ''
  });

  // Ingredients state
  const [ingredients, setIngredients] = useState([]);
  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [ingredientForm, setIngredientForm] = useState({
    name: '',
    packagingUnit: 'bag',
    measurementUnit: 'kg',
    amountPerPackage: 1,
    quantity: 0,
    usageType: 'brewing', // 'direct', 'brewing', 'both'
    autoDiscardEndOfDay: false, // Auto discard at end of day
    minStock: 0,
    recipeUnit: 'g',
    piecesPerPackage: 1,
    defaultPrice: 0,
    notes: ''
  });

  // Stock imports state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importRefreshKey, setImportRefreshKey] = useState(0);
  const [suppliesRefreshKey, setSuppliesRefreshKey] = useState(0);
  const [ingredientsRefreshKey, setIngredientsRefreshKey] = useState(0);
  const [importForm, setImportForm] = useState({
    items: [],
    supplier: '',
    notes: '',
    importDate: new Date().toISOString().slice(0, 10),
    paymentMethod: 'cash',
    shippingCost: ''
  });
  const [importInvoice, setImportInvoice] = useState(null);
  const [selectedImportItem, setSelectedImportItem] = useState({
    type: 'ingredient',
    itemId: '',
    optionName: '',
    quantity: 1,
    price: 0
  });
  
  // Payment method labels
  const paymentMethodLabels = {
    cash: 'Tiền mặt',
    transfer: 'Chuyển khoản',
    debt: 'Công nợ'
  };
  
  // Suppliers state
  const [suppliersList, setSuppliersList] = useState([]);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');

  const fileInputRef = useRef(null);

  // Labels
  const unitLabels = { piece: 'Cái', kg: 'Kg', bag: 'Túi' };
  const packagingLabels = { box: 'Hộp', can: 'Lon', bottle: 'Chai', bag: 'Túi', piece: 'Quả', item: 'Cái', kg: 'Kg' };
  const measurementLabels = { kg: 'Kg', g: 'Gram', liter: 'Lít', ml: 'ml', piece: 'Cái' };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle navigation state to open import modal
  useEffect(() => {
    if (location.state?.openImportModal) {
      setShowImportModal(true);
      setActiveTab('imports');
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadData = useCallback(async () => {
    try {
      if (activeTab === 'supplies') {
        const response = await axios.get(`${API_URL}/supplies`, { params: { limit: 0 } });
        setSupplies(response.data.data || []);
      } else if (activeTab === 'ingredients') {
        const response = await axios.get(`${API_URL}/ingredients`, { params: { limit: 0 } });
        setIngredients(response.data.data || []);
      } else if (activeTab === 'imports') {
        // Load supplies, ingredients, and suppliers for the create import modal
        const [suppliesRes, ingredientsRes, suppliersRes] = await Promise.all([
          axios.get(`${API_URL}/supplies`, { params: { limit: 0 } }),
          axios.get(`${API_URL}/ingredients`, { params: { limit: 0 } }),
          axios.get(`${API_URL}/suppliers`)
        ]);
        setSupplies(suppliesRes.data.data || []);
        setIngredients(ingredientsRes.data.data || []);
        setSuppliersList((suppliersRes.data.data || []).filter(s => !s.isFromImport));
      }
    } catch (error) {
      toast.error('Không thể tải dữ liệu');
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============ SUPPLIES FUNCTIONS ============
  const openSupplyModal = (supply = null) => {
    if (supply) {
      setEditingSupply(supply);
      setSupplyForm({
        name: supply.name,
        unit: supply.unit,
        piecesPerUnit: supply.piecesPerUnit || 1,
        quantity: supply.quantity || 0,
        options: supply.options || [],
        minStock: supply.minStock || 0,
        recipeUnit: supply.recipeUnit || 'unit',
        defaultPrice: supply.defaultPrice || 0,
        notes: supply.notes || ''
      });
    } else {
      setEditingSupply(null);
      setSupplyForm({
        name: '',
        unit: 'piece',
        piecesPerUnit: 1,
        quantity: 0,
        options: [],
        minStock: 0,
        recipeUnit: 'unit',
        defaultPrice: 0,
        notes: ''
      });
    }
    setShowSupplyModal(true);
  };

  const handleSupplySubmit = async (e) => {
    e.preventDefault();
    if (!supplyForm.name.trim()) {
      toast.error('Vui lòng nhập tên vật dụng');
      return;
    }

    try {
      if (editingSupply) {
        await axios.put(`${API_URL}/supplies/${editingSupply._id}`, supplyForm);
        toast.success('Đã cập nhật vật dụng');
      } else {
        await axios.post(`${API_URL}/supplies`, supplyForm);
        toast.success('Đã thêm vật dụng');
      }
      setShowSupplyModal(false);
      setSuppliesRefreshKey(prev => prev + 1);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi lưu vật dụng');
    }
  };

  const addSupplyOption = () => {
    setSupplyForm(prev => ({
      ...prev,
      options: [...prev.options, { name: '', quantity: 0 }]
    }));
  };

  const updateSupplyOption = (index, field, value) => {
    setSupplyForm(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeSupplyOption = (index) => {
    setSupplyForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // ============ INGREDIENTS FUNCTIONS ============
  const openIngredientModal = (ingredient = null) => {
    if (ingredient) {
      setEditingIngredient(ingredient);
      
      // Determine correct recipeUnit for legacy data
      let recipeUnit = ingredient.recipeUnit;
      if (!recipeUnit) {
        if (ingredient.packagingUnit === 'piece') {
          recipeUnit = 'piece';
        } else if (ingredient.packagingUnit === 'item' || ingredient.measurementUnit === 'piece') {
          recipeUnit = 'unit';
        } else {
          recipeUnit = 'g';
        }
      }
      
      // For measurementUnit = 'piece', piecesPerPackage should equal amountPerPackage
      const piecesPerPackage = ingredient.measurementUnit === 'piece' 
        ? (ingredient.amountPerPackage || 1)
        : (ingredient.piecesPerPackage || 1);
      
      setIngredientForm({
        name: ingredient.name,
        packagingUnit: ingredient.packagingUnit,
        measurementUnit: ingredient.measurementUnit,
        amountPerPackage: ingredient.amountPerPackage || 1,
        quantity: ingredient.quantity || 0,
        usageType: ingredient.usageType || (ingredient.isDirect ? 'direct' : 'brewing'),
        autoDiscardEndOfDay: ingredient.autoDiscardEndOfDay || false,
        minStock: ingredient.minStock || 0,
        recipeUnit: recipeUnit,
        piecesPerPackage: piecesPerPackage,
        defaultPrice: ingredient.defaultPrice || 0,
        notes: ingredient.notes || ''
      });
    } else {
      setEditingIngredient(null);
      setIngredientForm({
        name: '',
        packagingUnit: 'bag',
        measurementUnit: 'kg',
        amountPerPackage: 1,
        quantity: 0,
        usageType: 'brewing',
        autoDiscardEndOfDay: false,
        minStock: 0,
        recipeUnit: 'g',
        piecesPerPackage: 1,
        defaultPrice: 0,
        notes: ''
      });
    }
    setShowIngredientModal(true);
  };

  const handleIngredientSubmit = async (e) => {
    e.preventDefault();
    if (!ingredientForm.name.trim()) {
      toast.error('Vui lòng nhập tên nguyên liệu');
      return;
    }

    try {
      // Normalize data before submit
      const dataToSubmit = {
        ...ingredientForm,
        // When measurementUnit is 'piece', ensure piecesPerPackage equals amountPerPackage
        piecesPerPackage: ingredientForm.measurementUnit === 'piece' 
          ? ingredientForm.amountPerPackage 
          : ingredientForm.piecesPerPackage
      };
      
      if (editingIngredient) {
        await axios.put(`${API_URL}/ingredients/${editingIngredient._id}`, dataToSubmit);
        toast.success('Đã cập nhật nguyên liệu');
      } else {
        await axios.post(`${API_URL}/ingredients`, dataToSubmit);
        toast.success('Đã thêm nguyên liệu');
      }
      setShowIngredientModal(false);
      setIngredientsRefreshKey(prev => prev + 1);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi lưu nguyên liệu');
    }
  };

  // ============ STOCK IMPORTS FUNCTIONS ============
  const getSelectedItemUnit = () => {
    if (!selectedImportItem.itemId) return '';
    
    if (selectedImportItem.type === 'supply') {
      const supply = supplies.find(s => s._id === selectedImportItem.itemId);
      if (supply) return unitLabels[supply.unit] || supply.unit;
    } else {
      const ingredient = ingredients.find(i => i._id === selectedImportItem.itemId);
      if (ingredient) return packagingLabels[ingredient.packagingUnit] || ingredient.packagingUnit;
    }
    return '';
  };

  const openImportModal = () => {
    setImportForm({
      items: [],
      supplier: '',
      notes: '',
      importDate: new Date().toISOString().slice(0, 10),
      paymentMethod: 'cash',
      shippingCost: ''
    });
    setImportInvoice(null);
    setSelectedImportItem({
      type: 'ingredient',
      itemId: '',
      optionName: '',
      quantity: 1,
      price: 0
    });
    setShowImportModal(true);
  };

  const addImportItem = () => {
    if (!selectedImportItem.itemId) {
      toast.error('Vui lòng chọn mặt hàng');
      return;
    }

    let itemName = '';
    let displayUnit = '';

    if (selectedImportItem.type === 'supply') {
      const supply = supplies.find(s => s._id === selectedImportItem.itemId);
      if (supply) {
        itemName = supply.name + (selectedImportItem.optionName ? ` (${selectedImportItem.optionName})` : '');
        displayUnit = unitLabels[supply.unit];
      }
    } else {
      const ingredient = ingredients.find(i => i._id === selectedImportItem.itemId);
      if (ingredient) {
        itemName = ingredient.name;
        displayUnit = packagingLabels[ingredient.packagingUnit];
      }
    }

    setImportForm(prev => ({
      ...prev,
      items: [...prev.items, {
        ...selectedImportItem,
        itemName,
        displayUnit,
        totalPrice: selectedImportItem.quantity * selectedImportItem.price
      }]
    }));

    // Keep the same type for convenience when adding multiple items of same type
    setSelectedImportItem(prev => ({
      type: prev.type,
      itemId: '',
      optionName: '',
      quantity: 1,
      price: 0
    }));
  };

  const removeImportItem = (index) => {
    setImportForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (importForm.items.length === 0) {
      toast.error('Vui lòng thêm ít nhất 1 mặt hàng');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('items', JSON.stringify(importForm.items.map(item => ({
        itemType: item.type,
        itemId: item.itemId,
        optionName: item.optionName || null,
        quantity: item.quantity,
        price: item.price
      }))));
      formData.append('supplier', importForm.supplier);
      formData.append('notes', importForm.notes);
      formData.append('importDate', importForm.importDate);
      formData.append('paymentMethod', importForm.paymentMethod);
      formData.append('shippingCost', importForm.shippingCost || 0);
      
      if (importInvoice) {
        formData.append('invoice', importInvoice);
      }

      await axios.post(`${API_URL}/stock-imports`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Đã tạo phiếu nhập');
      setShowImportModal(false);
      setImportRefreshKey(prev => prev + 1); // Trigger refresh in StockImportsManagement
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi khi tạo phiếu nhập');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount) + 'đ';
  };

  // Add new supplier
  const handleAddSupplier = async () => {
    if (!newSupplierName.trim()) {
      toast.error('Vui lòng nhập tên NCC');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/suppliers`, { 
        name: newSupplierName.trim() 
      });
      const newSupplier = response.data.data;
      setSuppliersList(prev => [...prev, newSupplier]);
      setImportForm(prev => ({ ...prev, supplier: newSupplier.name }));
      setNewSupplierName('');
      setShowAddSupplier(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể thêm NCC');
    }
  };

  // Get supply with options for import dropdown
  const getSupplyOptionsForSelect = () => {
    const options = [];
    supplies.forEach(supply => {
      if (supply.options && supply.options.length > 0) {
        supply.options.forEach(opt => {
          options.push({
            id: supply._id,
            optionName: opt.name,
            label: `${supply.name} - ${opt.name}`,
            supply
          });
        });
      } else {
        options.push({
          id: supply._id,
          optionName: '',
          label: supply.name,
          supply
        });
      }
    });
    return options;
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : "p-6"}>
      {/* Content */}
      <div className={isMobile ? "px-4 pb-4 pt-4" : ""}>
        {/* SUPPLIES TAB */}
        {activeTab === 'supplies' && (
          <SuppliesListView 
            onAdd={() => openSupplyModal()}
            onEdit={(supply) => openSupplyModal(supply)}
            refreshKey={suppliesRefreshKey}
          />
        )}

        {/* INGREDIENTS TAB */}
        {activeTab === 'ingredients' && (
          <IngredientsListView 
            onAdd={() => openIngredientModal()}
            onEdit={(ingredient) => openIngredientModal(ingredient)}
            refreshKey={ingredientsRefreshKey}
          />
        )}

        {/* IMPORTS TAB */}
        {activeTab === 'imports' && (
          <StockImportsManagement 
            key={importRefreshKey}
            onCreateNew={openImportModal}
          />
        )}
      </div>


      {/* SUPPLY MODAL */}
      {showSupplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold">
                {editingSupply ? 'Sửa vật dụng' : 'Thêm vật dụng'}
              </h2>
              <button onClick={() => setShowSupplyModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <FiX size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSupplySubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tên vật dụng *</label>
                <input
                  type="text"
                  value={supplyForm.name}
                  onChange={(e) => setSupplyForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="VD: Cốc nhựa"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Đơn vị</label>
                <select
                  value={supplyForm.unit}
                  onChange={(e) => setSupplyForm(prev => ({ ...prev, unit: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="piece">Cái</option>
                  <option value="kg">Kg</option>
                  <option value="bag">Túi</option>
                </select>
              </div>
              
              {supplyForm.unit !== 'piece' && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    1 {unitLabels[supplyForm.unit]} = bao nhiêu cái?
                  </label>
                  <input
                    type="number"
                    value={supplyForm.piecesPerUnit}
                    onChange={(e) => setSupplyForm(prev => ({ ...prev, piecesPerUnit: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    min="1"
                  />
                </div>
              )}
              
              {/* Custom Options */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium">Size/Tùy chọn</label>
                  <button
                    type="button"
                    onClick={addSupplyOption}
                    className="text-sm text-green-600 hover:text-green-800"
                  >
                    + Thêm tùy chọn
                  </button>
                </div>
                
                {supplyForm.options.length === 0 ? (
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Số lượng tồn kho</label>
                    <input
                      type="number"
                      value={supplyForm.quantity}
                      onChange={(e) => setSupplyForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      min="0"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {supplyForm.options.map((opt, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={opt.name}
                          onChange={(e) => updateSupplyOption(idx, 'name', e.target.value)}
                          placeholder="VD: M, L, XL"
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        />
                        <input
                          type="number"
                          value={opt.quantity}
                          onChange={(e) => updateSupplyOption(idx, 'quantity', parseInt(e.target.value) || 0)}
                          placeholder="SL"
                          className="w-20 px-3 py-2 border rounded-lg text-sm"
                          min="0"
                        />
                        <button
                          type="button"
                          onClick={() => removeSupplyOption(idx)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Default Price and Recipe Unit - side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Giá mặc định (đ/{unitLabels[supplyForm.unit] || supplyForm.unit})
                  </label>
                  <input
                    type="number"
                    value={supplyForm.defaultPrice}
                    onChange={(e) => setSupplyForm(prev => ({ ...prev, defaultPrice: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    min="0"
                    placeholder="VD: 50000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Đơn vị công thức</label>
                  <select
                    value={supplyForm.recipeUnit}
                    onChange={(e) => setSupplyForm(prev => ({ ...prev, recipeUnit: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="g">Gam</option>
                    <option value="ml">Mililit</option>
                    <option value="piece">Quả</option>
                    <option value="unit">Cái</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Cảnh báo khi còn dưới</label>
                <input
                  type="number"
                  step="any"
                  value={supplyForm.minStock}
                  onChange={(e) => setSupplyForm(prev => ({ ...prev, minStock: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Ghi chú</label>
                <textarea
                  value={supplyForm.notes}
                  onChange={(e) => setSupplyForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="2"
                />
              </div>
              
              <button
                type="submit"
                className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
              >
                {editingSupply ? 'Cập nhật' : 'Thêm vật dụng'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* INGREDIENT MODAL */}
      {showIngredientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold">
                {editingIngredient ? 'Sửa nguyên liệu' : 'Thêm nguyên liệu'}
              </h2>
              <button onClick={() => setShowIngredientModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <FiX size={20} />
              </button>
            </div>
            
            <form onSubmit={handleIngredientSubmit} className="p-4 space-y-4">
              {/* Check if this is a brewing result ingredient */}
              {(() => {
                const isBrewingResult = editingIngredient?.fromRecipe || editingIngredient?.notes?.includes('công thức ủ');
                const measurementLabelsVi = { kg: 'Kg', g: 'Gam', liter: 'Lít', ml: 'ml', piece: 'Cái' };
                
                return (
                  <>
                    {/* Info banner for brewing result */}
                    {isBrewingResult && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center gap-2 text-purple-800">
                          <FiDroplet size={16} />
                          <span className="font-medium">Nguyên liệu từ công thức ủ</span>
                        </div>
                        <p className="text-xs text-purple-600 mt-1">
                          Tồn kho lưu theo: <strong>{measurementLabelsVi[ingredientForm.measurementUnit] || ingredientForm.measurementUnit}</strong> • 
                          Công thức dùng: <strong>{measurementLabelsVi[ingredientForm.recipeUnit] || ingredientForm.recipeUnit}</strong>
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Tên nguyên liệu *</label>
                      <input
                        type="text"
                        value={ingredientForm.name}
                        onChange={(e) => setIngredientForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="VD: Đường trắng"
                      />
                    </div>
                    
                    {/* For brewing result: simplified form */}
                    {isBrewingResult ? (
                      <>
                        {/* Quantity in measurement unit */}
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Số lượng tồn ({measurementLabelsVi[ingredientForm.measurementUnit] || ingredientForm.measurementUnit})
                          </label>
                          <input
                            type="number"
                            step="any"
                            value={ingredientForm.quantity}
                            onChange={(e) => setIngredientForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                            min="0"
                          />
                        </div>
                        
                        {/* Auto discard */}
                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={ingredientForm.autoDiscardEndOfDay}
                              onChange={(e) => setIngredientForm(prev => ({ ...prev, autoDiscardEndOfDay: e.target.checked }))}
                              className="mt-1 w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                            />
                            <div>
                              <span className="font-medium text-sm text-orange-800">Hủy lượng thừa cuối ngày</span>
                              <p className="text-xs text-orange-600">
                                Tồn kho còn lại sẽ tự động bị hủy vào cuối ngày (23:55) và ghi nhận vào Hàng hủy
                              </p>
                            </div>
                          </label>
                        </div>
                        
                        {/* Recipe unit - đơn vị sử dụng trong công thức pha chế */}
                        <div>
                          <label className="block text-sm font-medium mb-1">Đơn vị công thức</label>
                          <p className="text-xs text-gray-500 mb-2">
                            Đơn vị sử dụng khi thêm vào công thức pha chế (có thể khác với đơn vị lưu kho)
                          </p>
                          <select
                            value={ingredientForm.recipeUnit}
                            onChange={(e) => setIngredientForm(prev => ({ ...prev, recipeUnit: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                          >
                            {/* Show relevant units based on measurementUnit */}
                            {['liter', 'ml'].includes(ingredientForm.measurementUnit) ? (
                              <>
                                <option value="ml">Mililit (ml)</option>
                                <option value="liter">Lít</option>
                              </>
                            ) : ['kg', 'g'].includes(ingredientForm.measurementUnit) ? (
                              <>
                                <option value="g">Gam (g)</option>
                                <option value="kg">Kilogram (kg)</option>
                              </>
                            ) : (
                              <>
                                <option value="g">Gam (g)</option>
                                <option value="ml">Mililit (ml)</option>
                                <option value="piece">Quả</option>
                                <option value="unit">Cái</option>
                              </>
                            )}
                          </select>
                        </div>
                        
                        {/* Min stock warning */}
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Cảnh báo khi còn dưới ({measurementLabelsVi[ingredientForm.measurementUnit] || ingredientForm.measurementUnit})
                          </label>
                          <input
                            type="number"
                            step="any"
                            value={ingredientForm.minStock}
                            onChange={(e) => setIngredientForm(prev => ({ ...prev, minStock: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                            min="0"
                          />
                        </div>
                        
                        {/* Notes */}
                        <div>
                          <label className="block text-sm font-medium mb-1">Ghi chú</label>
                          <textarea
                            value={ingredientForm.notes}
                            onChange={(e) => setIngredientForm(prev => ({ ...prev, notes: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                            rows="2"
                          />
                        </div>
                      </>
                    ) : (
                      /* Normal ingredient form */
                      <>
                {/* Default Price and Recipe Unit */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Giá mặc định (đ/{packagingLabels[ingredientForm.packagingUnit] || ingredientForm.packagingUnit})
                    </label>
                    <input
                      type="number"
                      value={ingredientForm.defaultPrice}
                      onChange={(e) => setIngredientForm(prev => ({ ...prev, defaultPrice: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      min="0"
                      placeholder="VD: 50000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Đơn vị công thức</label>
                    {ingredientForm.packagingUnit === 'piece' ? (
                      <div className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-700">
                        Quả
                      </div>
                    ) : ingredientForm.packagingUnit === 'item' ? (
                      <div className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-700">
                        Cái
                      </div>
                    ) : (
                      <select
                        value={ingredientForm.recipeUnit}
                        onChange={(e) => setIngredientForm(prev => ({ ...prev, recipeUnit: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                      >
                        <option value="g">Gam</option>
                        <option value="ml">Mililit</option>
                        <option value="piece">Quả</option>
                        <option value="unit">Cái</option>
                      </select>
                    )}
                  </div>
                </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Đơn vị nhập</label>
                  <select
                    value={ingredientForm.packagingUnit}
                    onChange={(e) => {
                      const newUnit = e.target.value;
                      setIngredientForm(prev => ({
                        ...prev,
                        packagingUnit: newUnit,
                        // Auto-set values when packaging unit is 'piece', 'item' or 'kg'
                        ...(newUnit === 'piece' ? { 
                          measurementUnit: 'piece', 
                          amountPerPackage: 1,
                          usageType: 'direct',
                          recipeUnit: 'piece',
                          piecesPerPackage: 1
                        } : newUnit === 'item' ? {
                          measurementUnit: 'piece',
                          amountPerPackage: 1,
                          usageType: 'direct',
                          recipeUnit: 'unit',
                          piecesPerPackage: 1
                        } : newUnit === 'kg' ? {
                          measurementUnit: 'kg',
                          amountPerPackage: 1,
                          usageType: 'brewing'
                        } : {})
                      }));
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="bag">Túi</option>
                    <option value="box">Hộp</option>
                    <option value="can">Lon</option>
                    <option value="bottle">Chai</option>
                    <option value="piece">Quả</option>
                    <option value="item">Cái</option>
                    <option value="kg">Kg</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Số lượng tồn</label>
                  <input
                    type="number"
                    step="any"
                    value={ingredientForm.quantity}
                    onChange={(e) => setIngredientForm(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    min="0"
                  />
                </div>
              </div>
              
              {/* Measurement section - show when packaging unit is not 'piece', 'item' or 'kg' */}
              {!['piece', 'item', 'kg'].includes(ingredientForm.packagingUnit) && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Định lượng</label>
                    <select
                      value={ingredientForm.measurementUnit}
                      onChange={(e) => {
                        const newMeasurementUnit = e.target.value;
                        setIngredientForm(prev => ({ 
                          ...prev, 
                          measurementUnit: newMeasurementUnit,
                          // When measurementUnit is 'piece', auto-set recipeUnit to 'unit' and sync piecesPerPackage
                          ...(newMeasurementUnit === 'piece' ? {
                            recipeUnit: 'unit',
                            piecesPerPackage: prev.amountPerPackage
                          } : {})
                        }));
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      <option value="kg">Kg</option>
                      <option value="g">Gram</option>
                      <option value="liter">Lít</option>
                      <option value="ml">ml</option>
                      <option value="piece">Cái</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Mỗi {packagingLabels[ingredientForm.packagingUnit]} =
                    </label>
                    <div className="flex">
                      <input
                        type="number"
                        value={ingredientForm.amountPerPackage}
                        onChange={(e) => {
                          const newAmount = parseFloat(e.target.value) || 1;
                          setIngredientForm(prev => ({ 
                            ...prev, 
                            amountPerPackage: newAmount,
                            // Sync piecesPerPackage when measurementUnit is 'piece'
                            ...(prev.measurementUnit === 'piece' ? { piecesPerPackage: newAmount } : {})
                          }));
                        }}
                        className="flex-1 px-3 py-2 border rounded-l-lg focus:ring-2 focus:ring-green-500 min-w-0"
                        min="0.01"
                        step="0.01"
                      />
                      <span className="px-2 py-2 bg-gray-100 border border-l-0 rounded-r-lg text-sm whitespace-nowrap">
                        {measurementLabels[ingredientForm.measurementUnit]}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Pieces conversion - show when recipeUnit is 'piece' or 'unit' but packagingUnit is not 'piece' or 'item' 
                  AND measurementUnit is NOT 'piece' (because if measurementUnit is 'piece', amountPerPackage already defines pieces) */}
              {(ingredientForm.recipeUnit === 'piece' || ingredientForm.recipeUnit === 'unit') 
                && !['piece', 'item'].includes(ingredientForm.packagingUnit) 
                && ingredientForm.measurementUnit !== 'piece' && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <label className="block text-sm font-medium mb-2 text-yellow-800">
                    Số {ingredientForm.recipeUnit === 'piece' ? 'quả' : 'cái'} trong 1 {packagingLabels[ingredientForm.packagingUnit]}
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">1 {packagingLabels[ingredientForm.packagingUnit]} =</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={ingredientForm.piecesPerPackage}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                          setIngredientForm(prev => ({ ...prev, piecesPerPackage: val === '' ? '' : parseFloat(val) || 0 }));
                        }
                      }}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value) || 1;
                        setIngredientForm(prev => ({ ...prev, piecesPerPackage: Math.max(0.1, val) }));
                      }}
                      className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-center"
                    />
                    <span className="text-sm">{ingredientForm.recipeUnit === 'piece' ? 'quả' : 'cái'}</span>
                  </div>
                  <p className="text-xs text-yellow-700 mt-2">
                    ⚠️ Quan trọng: Nhập đúng số lượng để tính chi phí chính xác
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Loại nguyên liệu</label>
                <select
                  value={ingredientForm.usageType}
                  onChange={(e) => setIngredientForm(prev => ({ ...prev, usageType: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="brewing">Nguyên liệu ủ (VD: đường, sữa)</option>
                  <option value="direct">Nguyên liệu sử dụng trực tiếp (VD: trái cây nguyên quả)</option>
                  <option value="both">Cả hai</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {ingredientForm.usageType === 'brewing' && 'Dùng trong công thức ủ'}
                  {ingredientForm.usageType === 'direct' && 'Dùng trực tiếp trong công thức món'}
                  {ingredientForm.usageType === 'both' && 'Có thể dùng cả trong ủ và công thức món'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Cảnh báo khi còn dưới</label>
                <input
                  type="number"
                  step="any"
                  value={ingredientForm.minStock}
                  onChange={(e) => setIngredientForm(prev => ({ ...prev, minStock: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Ghi chú</label>
                <textarea
                  value={ingredientForm.notes}
                  onChange={(e) => setIngredientForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="2"
                />
              </div>
                      </>
                    )}
                  </>
                );
              })()}
              
              <button
                type="submit"
                className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
              >
                {editingIngredient ? 'Cập nhật' : 'Thêm nguyên liệu'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* IMPORT MODAL */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold">Tạo phiếu nhập hàng</h2>
              <button onClick={() => setShowImportModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <FiX size={20} />
              </button>
            </div>
            
            <form onSubmit={handleImportSubmit} className="p-4 space-y-4">
              {/* Add Item Section */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm text-blue-700">Thêm mặt hàng</p>
                  <div className="flex border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setSelectedImportItem(prev => ({ ...prev, type: 'ingredient', itemId: '', optionName: '' }))}
                      className={`px-3 py-1.5 text-xs ${selectedImportItem.type === 'ingredient' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}
                    >
                      Nguyên liệu
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedImportItem(prev => ({ ...prev, type: 'supply', itemId: '', optionName: '' }))}
                      className={`px-3 py-1.5 text-xs border-l ${selectedImportItem.type === 'supply' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                    >
                      Vật dụng
                    </button>
                  </div>
                </div>
                
                <div>
                  {selectedImportItem.type === 'supply' ? (
                    <ItemSearchSelect
                      itemType="supply"
                      supplyOptions={getSupplyOptionsForSelect()}
                      value={selectedImportItem.itemId}
                      optionValue={selectedImportItem.optionName}
                      onChange={(item) => {
                        if (item) {
                          setSelectedImportItem(prev => ({ 
                            ...prev, 
                            itemId: item.id, 
                            optionName: item.optionName || '' 
                          }));
                        } else {
                          setSelectedImportItem(prev => ({ ...prev, itemId: '', optionName: '' }));
                        }
                      }}
                      placeholder="Tìm vật dụng..."
                      showUnit={false}
                    />
                  ) : (
                    <ItemSearchSelect
                      itemType="ingredient"
                      items={ingredients}
                      value={selectedImportItem.itemId}
                      onChange={(item) => {
                        if (item) {
                          setSelectedImportItem(prev => ({ ...prev, itemId: item._id }));
                        } else {
                          setSelectedImportItem(prev => ({ ...prev, itemId: '' }));
                        }
                      }}
                      placeholder="Tìm nguyên liệu..."
                      showUnit={false}
                    />
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">
                      Số lượng{getSelectedItemUnit() ? ` (${getSelectedItemUnit()})` : ''}
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={selectedImportItem.quantity}
                      onChange={(e) => setSelectedImportItem(prev => ({ 
                        ...prev, 
                        quantity: parseFloat(e.target.value) || 1 
                      }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      min="0.01"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Đơn giá (đ)</label>
                    <input
                      type="number"
                      value={selectedImportItem.price}
                      onChange={(e) => setSelectedImportItem(prev => ({ 
                        ...prev, 
                        price: parseInt(e.target.value) || 0 
                      }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      min="0"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={addImportItem}
                      disabled={!selectedImportItem.itemId}
                      className="w-full py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      + Thêm
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Items List */}
              {importForm.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-green-50 border-b flex justify-between items-center">
                    <span className="font-medium text-sm text-green-700">
                      Danh sách ({importForm.items.length} mặt hàng)
                    </span>
                    <button
                      type="button"
                      onClick={() => setImportForm(prev => ({ ...prev, items: [] }))}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Xóa tất cả
                    </button>
                  </div>
                  <div className="divide-y max-h-48 overflow-y-auto">
                    {importForm.items.map((item, idx) => (
                      <div key={idx} className="p-3 flex justify-between items-center hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.itemName}</p>
                          <p className="text-xs text-gray-500">
                            {item.quantity} {item.displayUnit} × {formatCurrency(item.price)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className="font-semibold text-green-600 whitespace-nowrap">
                            {formatCurrency(item.totalPrice)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeImportItem(idx)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <FiX size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 bg-gray-50 flex justify-between border-t">
                    <span className="font-medium">Tổng cộng:</span>
                    <span className="font-bold text-green-600">
                      {formatCurrency(importForm.items.reduce((sum, i) => sum + i.totalPrice, 0))}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Additional Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Ngày nhập</label>
                  <div 
                    className="relative cursor-pointer"
                    onClick={() => {
                      const dateInput = document.getElementById('inventory-import-date');
                      if (dateInput) {
                        dateInput.showPicker();
                      }
                    }}
                  >
                    <input
                      type="text"
                      readOnly
                      value={importForm.importDate ? (() => {
                        const [y, m, d] = importForm.importDate.split('-');
                        return `${d}/${m}/${y}`;
                      })() : ''}
                      className="w-full px-3 py-2 pr-10 border rounded-lg bg-white cursor-pointer placeholder:text-gray-300 placeholder:text-xs"
                      placeholder="Ngày/Tháng/Năm"
                    />
                    <input
                      id="inventory-import-date"
                      type="date"
                      value={importForm.importDate}
                      onChange={(e) => setImportForm(prev => ({ ...prev, importDate: e.target.value }))}
                      className="sr-only"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nhà cung cấp</label>
                  {showAddSupplier ? (
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={newSupplierName}
                        onChange={(e) => setNewSupplierName(e.target.value)}
                        className="flex-1 min-w-0 px-2 py-2 border rounded-lg text-sm"
                        placeholder="Tên NCC mới"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleAddSupplier}
                        className="px-2 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 shrink-0"
                      >
                        Lưu
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowAddSupplier(false); setNewSupplierName(''); }}
                        className="px-2 py-2 border rounded-lg text-sm hover:bg-gray-50 shrink-0"
                      >
                        Hủy
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <select
                        value={importForm.supplier}
                        onChange={(e) => setImportForm(prev => ({ ...prev, supplier: e.target.value }))}
                        className="flex-1 min-w-0 px-2 py-2 border rounded-lg text-sm"
                      >
                        <option value="">-- Chọn NCC --</option>
                        {suppliersList.map(s => (
                          <option key={s._id} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddSupplier(true)}
                        className="px-2 py-2 border rounded-lg text-green-600 hover:bg-green-50 text-sm shrink-0"
                      >
                        + Thêm
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium mb-1">Phương thức thanh toán</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(paymentMethodLabels).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setImportForm(prev => ({ ...prev, paymentMethod: value }))}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                        importForm.paymentMethod === value
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Shipping Cost */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  <FiTruck className="inline mr-1" size={14} />
                  Phí vận chuyển
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={importForm.shippingCost}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, '');
                    setImportForm(prev => ({ ...prev, shippingCost: val }));
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  * Phí vận chuyển sẽ tự động được tính vào chi phí khác
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Ghi chú</label>
                <textarea
                  value={importForm.notes}
                  onChange={(e) => setImportForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                />
              </div>
              
              {/* Invoice Upload */}
              <div>
                <label className="block text-sm font-medium mb-1">Hóa đơn (ảnh/PDF)</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setImportInvoice(e.target.files[0])}
                  accept="image/*,.pdf"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 flex items-center justify-center gap-2 text-gray-600"
                >
                  <FiUpload size={16} />
                  {importInvoice ? importInvoice.name : 'Tải lên hóa đơn'}
                </button>
              </div>
              
              <button
                type="submit"
                disabled={importForm.items.length === 0}
                className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium disabled:bg-gray-300"
              >
                Tạo phiếu nhập
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
